import { Component, ElementRef, ViewChild } from '@angular/core';
import { IonicPage, NavParams } from 'ionic-angular';
import { SupportVideo, SupportVideoChapter } from '../../../models/support/support.models';
import { SupportVideoCatalogService } from '../../../services/support/support-video-catalog.service';

@IonicPage({
  name: 'SupportVideoPlayerPage',
  segment: 'support-video-player/:videoId'
})
@Component({
  selector: 'page-support-video-player',
  templateUrl: 'support-video-player.html'
})
export class SupportVideoPlayerPage {
  @ViewChild('videoPlayer') videoPlayer: ElementRef;

  video: SupportVideo;
  currentTime: string = '00:00';
  duration: string = '00:00';
  loading: boolean = true;
  activeChapterEndTime: number = null;
  activeChapter: SupportVideoChapter;
  chapterComplete: boolean;
  completedChapter: SupportVideoChapter;
  private programmaticSeekInProgress: boolean;
  private initialStartTime: number;
  private initialEndTime: number;
  private initialTimeApplied: boolean;

  constructor(
    private navParams: NavParams,
    private supportVideoCatalogService: SupportVideoCatalogService
  ) {
    const videoId = Number(this.navParams.get('videoId'));
    this.video = this.supportVideoCatalogService.getVideoById(videoId);
    this.loading = !!this.video;
    this.initialStartTime = Number(this.navParams.get('start'));
    this.initialEndTime = Number(this.navParams.get('end'));
  }

  get chapters(): SupportVideoChapter[] {
    return this.video && this.video.chapters ? this.video.chapters : [];
  }

  onLoadedMetadata() {
    const player = this.getPlayer();

    if (!player) {
      return;
    }

    this.duration = this.formatTime(player.duration);
    this.currentTime = this.formatTime(player.currentTime);
    this.loading = false;

    this.applyInitialTime(player);
  }

  onTimeUpdate() {
    const player = this.getPlayer();

    if (!player) {
      return;
    }

    this.currentTime = this.formatTime(player.currentTime);

    if (this.activeChapterEndTime !== null && player.currentTime >= this.activeChapterEndTime) {
      player.pause();
      this.chapterComplete = true;
      this.completedChapter = this.activeChapter;

      if (Math.abs(player.currentTime - this.activeChapterEndTime) > .15) {
        this.programmaticSeekInProgress = true;
        player.currentTime = this.activeChapterEndTime;
      }

      return;
    }

    this.updateActiveChapter(player.currentTime);
  }

  onWaiting() {
    this.loading = true;
  }

  onCanPlay() {
    this.loading = false;
  }

  playChapter(chapter: SupportVideoChapter) {
    const player = this.getPlayer();

    if (!player || !chapter) {
      return;
    }

    this.chapterComplete = false;
    this.completedChapter = null;
    this.setActiveChapter(chapter);
    this.programmaticSeekInProgress = true;
    player.currentTime = chapter.startTime;
    player.play();
  }

  replayChapter() {
    if (!this.completedChapter) {
      return;
    }

    this.playChapter(this.completedChapter);
  }

  continueVideo() {
    const player = this.getPlayer();

    if (!player) {
      return;
    }

    this.chapterComplete = false;
    this.completedChapter = null;
    this.activeChapterEndTime = null;

    const playPromise = player.play();

    if (playPromise && playPromise.catch) {
      playPromise.catch(() => {});
    }
  }

  onPlay() {
    if (!this.chapterComplete) {
      return;
    }

    this.chapterComplete = false;
    this.completedChapter = null;
    this.activeChapterEndTime = null;
  }

  onSeeking() {
    if (this.programmaticSeekInProgress) {
      return;
    }

    this.chapterComplete = false;
    this.completedChapter = null;

    const player = this.getPlayer();
    const currentTime = player ? player.currentTime : 0;

    if (this.activeChapter && this.activeChapter.endTime) {
      if (currentTime >= this.activeChapter.startTime && currentTime < this.activeChapter.endTime) {
        this.activeChapterEndTime = this.activeChapter.endTime;
        return;
      }

      this.activeChapterEndTime = null;
      this.activeChapter = null;
    }

    this.updateActiveChapter(currentTime);
  }

  onSeeked() {
    this.programmaticSeekInProgress = false;
    const player = this.getPlayer();

    if (player) {
      this.updateActiveChapter(player.currentTime);
    }
  }

  formatChapterTime(chapter: SupportVideoChapter): string {
    if (!chapter || !chapter.endTime) {
      return this.formatTime(chapter ? chapter.startTime : 0);
    }

    return `${this.formatTime(chapter.startTime)} - ${this.formatTime(chapter.endTime)}`;
  }

  isActiveChapter(chapter: SupportVideoChapter): boolean {
    return this.activeChapter === chapter;
  }

  private getPlayer(): HTMLVideoElement {
    return this.videoPlayer ? this.videoPlayer.nativeElement as HTMLVideoElement : null;
  }

  private applyInitialTime(player: HTMLVideoElement) {
    if (this.initialTimeApplied || !isFinite(this.initialStartTime) || this.initialStartTime <= 0) {
      return;
    }

    this.initialTimeApplied = true;
    this.setActiveChapter(this.findChapterByRange(this.initialStartTime, this.initialEndTime));
    this.programmaticSeekInProgress = true;

    player.currentTime = this.initialStartTime;
    this.currentTime = this.formatTime(this.initialStartTime);

    const playPromise = player.play();

    if (playPromise && playPromise.catch) {
      playPromise.catch(() => {});
    }
  }

  private setActiveChapter(chapter: SupportVideoChapter) {
    this.activeChapter = chapter || null;
    this.activeChapterEndTime = chapter && chapter.endTime ? chapter.endTime : null;
  }

  private updateActiveChapter(currentTime: number) {
    if (this.activeChapter && this.activeChapter.endTime && currentTime >= this.activeChapter.startTime && currentTime < this.activeChapter.endTime) {
      return;
    }

    const chapter = this.chapters.find(item =>
      item.endTime &&
      currentTime >= item.startTime &&
      currentTime < item.endTime
    );

    if (chapter) {
      this.setActiveChapter(chapter);
      return;
    }

    if (this.activeChapter && this.activeChapter.endTime && currentTime >= this.activeChapter.endTime) {
      return;
    }

    this.setActiveChapter(null);
  }

  private findChapterByRange(startTime: number, endTime: number): SupportVideoChapter {
    if (!isFinite(startTime)) {
      return null;
    }

    const exactMatch = this.chapters.find(chapter =>
      chapter.startTime === startTime &&
      (!isFinite(endTime) || chapter.endTime === endTime)
    );

    if (exactMatch) {
      return exactMatch;
    }

    return this.chapters.find(chapter =>
      chapter.endTime &&
      startTime >= chapter.startTime &&
      startTime < chapter.endTime
    );
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return '00:00';
    }

    const wholeSeconds = Math.floor(seconds);
    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const remainingSeconds = wholeSeconds % 60;

    if (hours > 0) {
      return `${hours}:${this.padTime(minutes)}:${this.padTime(remainingSeconds)}`;
    }

    return `${this.padTime(minutes)}:${this.padTime(remainingSeconds)}`;
  }

  private padTime(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}



// WEBPACK FOOTER //
// ./src/pages/support/support-video-player/support-video-player.ts